import { EnumDestinatarioTipoDestinatarioEnum } from '@framework/api-client/api/inquiries-compile/schemas/enumDestinatarioTipoDestinatarioEnum.ts';
import { type EnumTipoMittenteDTOCodice } from '@framework/api-client/api/inquiries-compile/schemas/enumTipoMittenteDTOCodice.ts';
import { type TipoCompetenzaSecondariaDTO } from '@framework/api-client/api/users/schemas/tipoCompetenzaSecondariaDTO.ts';
import { useMemo } from 'react';
import { useCommunicationsContext } from '../context/CommunicationsContext.tsx';

/**
 * Funzione per normalizzare il tipo di utente di una comunicazione.
 * Dividiamo l'hook in una funzione per poterlo testare facilmente.
 */
export function normalizeCommunicationUserType(params: {
  userType:
    | EnumDestinatarioTipoDestinatarioEnum
    | EnumTipoMittenteDTOCodice
    | null
    | undefined;
  isSubprocedure: boolean;
  isSourceFromCurrentProcedure: boolean;
  isSourceFromParentProcedure: boolean;
}) {
  const {
    userType,
    isSourceFromCurrentProcedure,
    isSourceFromParentProcedure,
    isSubprocedure,
  } = params;
  if (!userType) return null;

  if (!isSubprocedure) return userType;

  // Se ci troviamo in un endoprocedimento, andiamo a sostituire il tipo dei tag per "invertirli"
  // Se tag è ENTE_TERZO e ufficio è responsabile endoprocedimento -> sostituisco con tag UFFICIO_RESPONSABILE
  if (
    userType ===
      EnumDestinatarioTipoDestinatarioEnum.ENDOPROCEDIMENTI_PRIMO_LIVELLO &&
    isSourceFromCurrentProcedure
  ) {
    return EnumDestinatarioTipoDestinatarioEnum.RESPONSABILE_PROCEDIMENTO;
  }

  // Se ci troviamo in un endoprocedimento, andiamo a sostituire il tipo dei tag per "invertirli"
  // Se tag è UFFICIO_RESPONSABILE -> sostituisco con tag UFFICIO_RICHIEDENTE
  if (
    userType ===
      EnumDestinatarioTipoDestinatarioEnum.RESPONSABILE_PROCEDIMENTO &&
    isSourceFromParentProcedure
  ) {
    return EnumDestinatarioTipoDestinatarioEnum.UFFICIO_RICHIEDENTE;
  }

  // Altrimenti mantengo il tipo originale
  return userType;
}

/**
 * Mappiamo il tipo di mittente/destinatario in base al contesto della comunicazione.
 *
 * e.g.
 * Un mittente "Responsabile procedimento" per un utente di un endoprocedimento
 * diventa "Ufficio richiedente" (il responsabile procedimento è l'ufficio che ha in carico il procedimento padre
 * dell'endoprocedimento, quindi quello "richiedente").
 */
export function useNormalizedCommunicationUserType(
  userType:
    | EnumDestinatarioTipoDestinatarioEnum
    | EnumTipoMittenteDTOCodice
    | null
    | undefined,
  /**
   * ID del procedimento collegato al mittente/destinatario.
   */
  sourceProcedureId: string | null | undefined,
  /**
   * Tipo Competenza Secondaria del mittente/destinatario, da cui
   * si ricava la denominazione da mostrare come testo secondario.
   */
  competenceType: TipoCompetenzaSecondariaDTO | null | undefined,
) {
  const { parentProcedureId, procedureId } = useCommunicationsContext();

  const isSubprocedure = Boolean(parentProcedureId);
  // Se il mittente/destinatario è collegato al procedimento corrente (endoprocedimento)
  const isSourceFromCurrentProcedure =
    !!sourceProcedureId && procedureId === sourceProcedureId;

  // Se il mittente/destinatario è collegato al procedimento padre
  const isSourceFromParentProcedure =
    !!sourceProcedureId && parentProcedureId === sourceProcedureId;

  const normalizedCommunicationUserType = useMemo(
    () =>
      normalizeCommunicationUserType({
        isSourceFromCurrentProcedure,
        isSourceFromParentProcedure,
        isSubprocedure,
        userType,
      }),
    [
      userType,
      isSubprocedure,
      isSourceFromCurrentProcedure,
      isSourceFromParentProcedure,
    ],
  );

  return {
    type: normalizedCommunicationUserType,
    secondaryText: competenceType?.denominazione ?? undefined,
  };
}
